/**
 * MfaCodeInput — the ONE six-digit second-factor code field (TOTP, SMS and email codes).
 *
 * Public since LIFE-SHORTLISTED-001 D12a. Eleven hand-built copies of this field existed beside it;
 * this is the one they converge on, so it carries what each of them needs and nothing a host has to
 * remember.
 *
 * ## It IS the labelled field
 *
 * It composes page-shell's `<FormField>` (the one labelled field): the visible label is linked to
 * the input and is its only accessible name, and `required` marks both. A host never wraps it in a
 * `<FormField>` of its own.
 *
 * ## `autoSubmit` has no default
 *
 * Submitting on the sixth digit is right on a sign-in challenge and destructive on a form that
 * regenerates backup codes or turns MFA off. So every host states it, and a missing value throws at
 * render (React 19 no longer runs `propTypes`, so a prop-type check would never fire).
 *
 * ## One submit path
 *
 * Inside a `<form>` the form is the path: do not pass `onSubmit`; the form's own `onSubmit(event)`
 * runs once, for Enter and for auto-submit alike. Outside a form, pass `onSubmit`; it is called with
 * no argument. See `hooks/useCodeEntrySubmit.js` for the whole contract.
 *
 * ## The refusal is the host's alert
 *
 * A refused code is often more than a field error (a locked account, an expired session, an alert
 * with its own action), and ten of the eleven sites already render one. So the field renders no
 * error text: pass `error` (it marks the input invalid) and `errorId`, the id of the element that
 * shows the refusal (it describes the input WHILE `error` is set: with no refusal the host renders no
 * alert, and the input must never be described by an element that is not in the document).
 *
 * @module @puffin-core/mfa-ui/MfaCodeInput
 */

import { useCallback, useId, useRef } from 'react';
import PropTypes from 'prop-types';
// The NARROW subpath, never the page-shell barrel (see BackupCodesCard.jsx).
import { FormField } from '@puffin-core/page-shell/form-field';
import { useCodeEntryFocus } from './hooks/useCodeEntryFocus.js';
import { requestHostSubmit, useCodeEntrySubmit } from './hooks/useCodeEntrySubmit.js';
import { useMfaAutoSubmit } from './hooks/useMfaAutoSubmit.js';

/** Digits in a TOTP, SMS or email verification code. */
const CODE_LENGTH = 6;

/**
 * Why this `autoSubmit` cannot be rendered, or null when it can.
 * @param {*} autoSubmit - the prop as passed
 * @returns {string|null}
 */
function autoSubmitRefusal(autoSubmit) {
  if (typeof autoSubmit === 'boolean') return null;
  return 'MfaCodeInput: `autoSubmit` must be stated as true or false. It has no default: on a form that regenerates or removes something, submitting on the sixth digit would do it with no confirmation.';
}

/**
 * @param {Object} props
 * @param {string} props.code - the current code
 * @param {(code: string) => void} props.onChange - receives digits only, six at most
 * @param {React.ReactNode} props.label - the visible label; the input's accessible name
 * @param {boolean} props.autoSubmit - REQUIRED, no default: submit when the sixth digit lands
 * @param {() => void} [props.onSubmit] - the submit path OUTSIDE a form; never passed inside one
 * @param {number} [props.autoSubmitDelay=300] - the pause before an auto-submit, in ms
 * @param {string} [props.id] - the input's id; unique per instance when omitted
 * @param {boolean} [props.focusOnMount=false] - take focus when the field mounts
 * @param {boolean} [props.isLoading=false] - an attempt is in flight: no Enter, no auto-submit
 * @param {boolean} [props.disabled] - disables the input; defaults to `isLoading`
 * @param {string|Error|null} [props.error=null] - marks the input invalid; its text is the host's
 * @param {string} [props.errorId] - id of the host element that shows the refusal; read only while
 *   `error` is set
 * @param {string} [props.placeholder]
 * @param {boolean} [props.required=false] - marks the label and sets `aria-required`
 * @param {string} [props.data-testid='mfa-code-input']
 * @returns {React.JSX.Element}
 */
export function MfaCodeInput({
  code,
  onChange,
  label,
  autoSubmit,
  onSubmit,
  autoSubmitDelay = 300,
  id,
  focusOnMount = false,
  isLoading = false,
  disabled,
  error = null,
  errorId,
  placeholder,
  required = false,
  'data-testid': testId = 'mfa-code-input',
}) {
  const refusal = autoSubmitRefusal(autoSubmit);
  if (refusal) throw new Error(refusal);

  const inputRef = useRef(null);
  const busy = isLoading || Boolean(disabled);

  const handleChange = useCallback(
    (event) => onChange(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH)),
    [onChange],
  );

  const submit = useCallback(() => requestHostSubmit(inputRef.current, onSubmit), [onSubmit]);

  // `useId` reads as a custom hook to `web/component-state-order`, so it follows the callbacks.
  const generatedId = useId();
  useCodeEntryFocus({ inputRef, code, focusOnMount });

  const { disarm } = useMfaAutoSubmit({
    code,
    codeLength: CODE_LENGTH,
    isLoading: busy,
    enabled: autoSubmit,
    onSubmit: submit,
    delay: autoSubmitDelay,
  });

  const handleKeyDown = useCodeEntrySubmit({
    inputRef,
    component: 'MfaCodeInput',
    complete: code.length === CODE_LENGTH && !busy,
    onSubmit,
    onSubmitted: disarm,
  });

  return (
    <FormField id={id ?? generatedId} label={label} required={required}>
      {(control) => (
        <input
          ref={inputRef}
          id={control.id}
          data-testid={testId}
          className="form-input mfa-code-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={CODE_LENGTH}
          autoComplete="one-time-code"
          placeholder={placeholder}
          value={code}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled ?? isLoading}
          aria-required={control['aria-required']}
          aria-invalid={Boolean(error)}
          aria-describedby={error && errorId ? errorId : undefined}
        />
      )}
    </FormField>
  );
}

MfaCodeInput.propTypes = {
  code: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.node.isRequired,
  autoSubmit: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func,
  autoSubmitDelay: PropTypes.number,
  id: PropTypes.string,
  focusOnMount: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Error)]),
  errorId: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  'data-testid': PropTypes.string,
};
